import React, { createContext, useState, useEffect, useContext } from 'react';
import { api } from '../../config';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [authChecked, setAuthChecked] = useState(false);

    const fetchUser = async () => {
        // Prevent double-checking if already done
        if (authChecked) return;

        try {
            // Cookies are sent automatically with withCredentials: true
            const response = await api.get('/me');

            if (response.data.authenticated && response.data.user) {
                setUser(response.data.user);
                localStorage.setItem('vendorUser', JSON.stringify(response.data.user));
                if (response.data.token) {
                    localStorage.setItem('authToken', response.data.token);
                }
            } else {
                // API returned but user not authenticated - clear everything
                setUser(null);
                localStorage.removeItem('vendorUser');
            }
        } catch (error) {
            console.error('Auth check failed:', error);

            // On 401/403, the cookie is invalid - clear everything, don't use fallback
            if (error.response?.status === 401 || error.response?.status === 403) {
                localStorage.removeItem('vendorUser');
                setUser(null);
            } else {
                // For network errors only, try to use stored user as temporary fallback
                const storedUser = localStorage.getItem('vendorUser');
                if (storedUser) {
                    try {
                        const parsed = JSON.parse(storedUser);
                        console.log('Network error - using stored user temporarily');
                        setUser(parsed);
                    } catch {
                        setUser(null);
                        localStorage.removeItem('vendorUser');
                    }
                } else {
                    setUser(null);
                }
            }
        } finally {
            setLoading(false);
            setAuthChecked(true);
        }
    };

    useEffect(() => {
        fetchUser();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const login = (userData, token) => {
        setUser(userData);
        if (userData) {
            localStorage.setItem('vendorUser', JSON.stringify(userData));
        }
        if (token) {
            localStorage.setItem('authToken', token);
        }
    };

    const logout = async () => {
        try {
            // Call logout endpoint - server will clear the HTTP-only cookie
            await api.post('/logout');
        } catch (error) {
            console.error('Logout failed:', error);
        } finally {
            setUser(null);
            localStorage.removeItem('vendorUser');
            localStorage.removeItem('authToken');
        }
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, logout, fetchUser }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
